// Locks the PDP delivery-method choice to the method the cart already holds.
//
// An order is one method at one location — a cart can be pickup or delivery, never both. Nothing
// enforced that at add-to-cart time: with a pickup line already in the cart, a buyer could still
// pick Delivery here and build a mixed cart, which only surfaced as a warning on the cart page and
// as an unfulfillable order after checkout (FPS-682).
//
// The cart's method is authoritative. The opposing radio is disabled while the cart holds items,
// and the only way out is emptying the cart — a conversion would have to re-check every line for
// stock, store and radius against the new method, which is a separate piece of work.
if (!customElements.get('cart-method-lock')) {
  customElements.define(
    'cart-method-lock',
    class CartMethodLock extends HTMLElement {
      constructor() {
        super();
        this._refreshToken = 0;
        this._restoring = false;
      }

      connectedCallback() {
        this.message = this.querySelector('[ref="message"]');
        this.emptyCartButton = this.querySelector('[ref="emptyCart"]');

        this._onEmptyCart = () => this.emptyCart();
        this.emptyCartButton?.addEventListener('click', this._onEmptyCart);

        this._onCartChanged = () => {
          // apply() below restores the cart's own method, and that restore reaches this listener
          // as `location:changed`. Refetching the cart it was just read from is pure waste.
          if (this._restoring) return;
          this.refresh();
        };
        document.addEventListener('cart:updated', this._onCartChanged);
        document.addEventListener('cart:refresh', this._onCartChanged);
        // The note names the store/address, so it has to re-render when either changes.
        document.addEventListener('location:changed', this._onCartChanged);
        document.addEventListener('deliveryAddress:updated', this._onCartChanged);

        this.refresh();
      }

      disconnectedCallback() {
        this.emptyCartButton?.removeEventListener('click', this._onEmptyCart);
        document.removeEventListener('cart:updated', this._onCartChanged);
        document.removeEventListener('cart:refresh', this._onCartChanged);
        document.removeEventListener('location:changed', this._onCartChanged);
        document.removeEventListener('deliveryAddress:updated', this._onCartChanged);
      }

      // Only the newest /cart.js response may touch the lock. Refreshes overlap routinely — an
      // add-to-cart and a location change land within a tick of each other — and an older response
      // resolving last would apply a stale cart. The costly direction is stale-empty over
      // fresh-pickup: apply(null) re-enables every radio, so the lock disappears on a cart that
      // has items and the buyer can build the mixed cart this component exists to prevent.
      async refresh() {
        const token = ++this._refreshToken;
        let method;
        try {
          method = await this.cartMethod();
        } catch {
          // A cart we could not read is not a cart with no method to lock to. Leave the lock
          // exactly as it stands: unlocking here would re-enable both radios on what may well be
          // a non-empty cart, which is the mixed cart this component exists to prevent.
          return;
        }
        if (token !== this._refreshToken) return;
        // A quick-view instance can be torn down mid-fetch (quick-view.js empties the drawer on
        // hide). Its own token is untouched by that, so the response still arrives and would go
        // on to mutate radios for a product the buyer has closed.
        if (!this.isConnected) return;
        this.apply(method);
      }

      // The method every line in the cart shares, or null when there is nothing to lock to.
      // Throws when the cart cannot be read — refresh() holds the lock rather than dropping it.
      async cartMethod() {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`/cart.js responded ${response.status}`);
        const cart = await response.json();
        const methods = new Set(
          (cart.items || [])
            // `_delivery_method` is canonical, `delivery_method` the legacy key. Carts in the
            // wild hold both, and reading only the canonical one made a legacy cart look
            // methodless — leaving the PDP unlocked on exactly the older carts most likely to
            // be mixed. Same pair FPS-708's checkout Function and FPS-709 read.
            .map((item) => item.properties?._delivery_method || item.properties?.delivery_method)
            .filter(Boolean)
        );
        // 0 → empty cart, or lines from before either property existed. >1 → a mixed cart
        // assembled before this gate shipped. Locking to a guess would trap the buyer.
        return methods.size === 1 ? [...methods][0] : null;
      }

      apply(cartMethod) {
        // Fail closed rather than falling back to `document`. Every render site nests this inside
        // <delivery-options> (pdp-delivery-options.liquid), so a miss means the markup moved — and
        // a document-wide query would then reach into a sibling instance (PDP + open quick-view are
        // both live at once) and unlock radios this element does not own.
        const scope = this.closest('delivery-options');
        if (!scope) return;
        const radios = [...scope.querySelectorAll('input[name^="delivery-option"]')];

        radios.forEach((radio) => {
          radio.disabled = false;
          radio.closest('.delivery-option')?.removeAttribute('data-method-locked');
        });

        if (!cartMethod) {
          this.removeAttribute('data-locked');
          return;
        }

        const matching = radios.find((radio) => radio.value === cartMethod);
        const lockedOut = radios.filter((radio) => radio !== matching);

        // A pickup-only product (FPS-516 drops the Delivery radio) in a delivery cart matches
        // nothing: every radio locks, and the add-to-cart gate stays shut for want of a selection.
        if (!lockedOut.length && matching) {
          this.removeAttribute('data-locked');
          return;
        }

        lockedOut.forEach((radio) => {
          radio.disabled = true;
          radio.closest('.delivery-option')?.setAttribute('data-method-locked', '');
        });

        if (matching && !matching.checked) {
          matching.checked = true;
          // delivery-options.js re-evaluates the submit gate off this event, so it has to fire.
          // Flagged because the FPS-677 conflict gate also listens for a delivery radio turning
          // on, and this one is the lock restoring the cart's own method — not a buyer choice.
          const restoreEvent = new Event('change', { bubbles: true });
          restoreEvent.cartMethodLockRestore = true;
          // delivery-options' handler dispatches `location:changed` synchronously off this, which
          // this component listens for — so the restore would refetch the cart it was read from.
          // finally, not a bare reset: a listener that throws would otherwise strand the flag on
          // and silently drop every future refresh.
          this._restoring = true;
          try {
            matching.dispatchEvent(restoreEvent);
          } finally {
            this._restoring = false;
          }
        }

        this.renderNote(cartMethod);
        this.setAttribute('data-locked', '');
      }

      renderNote(cartMethod) {
        const isPickup = cartMethod === 'pickup';
        const location = isPickup ? this.pickupStoreName() : this.deliveryAddress();

        let template;
        if (isPickup) {
          template = location ? this.dataset.i18nPickupAt : this.dataset.i18nPickup;
        } else {
          template = location ? this.dataset.i18nDeliveryTo : this.dataset.i18nDelivery;
        }

        if (this.message) {
          this.message.textContent = (template || '').replace('__LOCATION__', location || '');
        }
        if (this.emptyCartButton) {
          this.emptyCartButton.textContent = isPickup
            ? this.dataset.i18nSwitchToDelivery
            : this.dataset.i18nSwitchToPickup;
        }
      }

      async emptyCart() {
        const confirmation = this.dataset.i18nConfirm;
        if (confirmation && !window.confirm(confirmation)) return;

        try {
          await fetch('/cart/clear.js', { method: 'POST' });
        } catch {
          // Fall through to the reload — it re-reads the cart either way.
        }
        window.location.reload();
      }

      pickupStoreName() {
        const parsed = this.readCookie('locationDrawerSelected');
        if (!parsed) return null;
        // Two historical shapes, same as cart-fulfilment-location.js / location-status.js:
        //   legacy flat → { latitude, longitude, name }
        //   per-method  → { pickup: { ... }, ... }
        const selection = parsed.latitude === undefined && parsed.pickup ? parsed.pickup : parsed;
        return selection?.name?.trim() || null;
      }

      deliveryAddress() {
        const parsed = this.readCookie('addressDrawerResult');
        return parsed?.formatted_address?.trim() || parsed?.name?.trim() || null;
      }

      readCookie(name) {
        const prefix = name + '=';
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        try {
          return JSON.parse(decodeURIComponent(cookie.substring(prefix.length)));
        } catch {
          return null;
        }
      }
    },
  );
}
